import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import { withRouter } from 'react-router-dom';
import { Flex, Spinner, Suspense } from 'core/components';
import Page from 'app/components/page/Page';
import $recentlyViewed from 'app/stores/recentlyViewed/$recentlyViewed';
import PanelEditView from 'app/views/boards/PanelEditView';
import BoardView from 'app/views/boards/BoardView';
import TextPanelEditorDialog from 'app/views/boards/panels/text/TextPanelEditorDialog';

@withRouter
@inject('$auth', '$boards', '$journeys')
@observer
class BoardWrapperView extends Component {
  state = {
    loadingBoard: true,
    board: null,
    panel: null,
    editingTextPanel: null,
    isNewTextPanel: false
  };

  componentDidMount() {
    // Watch for AI tool results that modify boards and auto-refresh
    const { $journeys } = this.props;
    this._toolResultDisposer = reaction(
      () => $journeys.lastBoardToolResult,
      (result) => this.handleBoardToolResult(result)
    );

    return Promise.resolve()
      .then(() => this.setBoard())
      .then((board) => this.setPanel(board));
  }

  componentWillUnmount() {
    const { board } = this.state;
    board?.cleanupQueryModels();
    this.removeBoardAiContext();
    if (this._toolResultDisposer) {
      this._toolResultDisposer();
    }
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const { boardId } = match.params;
    const { panelId } = match.params;
    const prevBoardId = prevProps.match.params.boardId;
    const prevPanelId = prevProps.match.params.panelId;

    return Promise.resolve()
      .then(() => {
        const { board } = this.state;
        if (boardId !== prevBoardId) {
          return this.setBoard();
        }
        return board;
      })
      .then((board) => {
        if (panelId !== prevPanelId) {
          return this.setPanel(board);
        }
        return undefined;
      });
  }

  async setBoard() {
    const { $boards, history } = this.props;
    const { board: prevBoard } = this.state;

    let board = null;

    // Creating a new dashboard (perhaps because there's no other option!)
    // TODO: this i_ check/behavior is something we'd like to ultimately do away with by not showing the IDs in the URL until the board is actually persisted
    if (this.boardId === 'new' || (this.boardId?.startsWith('i_') && !prevBoard)) {
      board = $boards.personalBoards.get(this.boardId) || $boards.collection.forge();
      board.set({ config: { ...board.get('config'), isDashboard: !this.isUde } });
      this.setState({ board, loadingBoard: false });
      if (this.isUde) {
        // Force a new panel in here
        history.replace(`${this.basePath}/boards/${board.id}/panel/ude`);
      }
    } else {
      board = prevBoard;
      if (!prevBoard || prevBoard.id !== this.boardId) {
        board = $boards.collection.get(this.boardId) || $boards.collection.forge({ id: this.boardId });
      }

      // Let's only take action if we didn't arrive at the same board
      if (board !== prevBoard) {
        // Clean up query models from the previous board before loading the new one
        prevBoard?.cleanupQueryModels();

        try {
          // We only need to fetch the board if it has persisted state and it's only recently viewed if it's saved
          if (!board.isNew) {
            await board.fetch();
            $recentlyViewed.add({ view_type: board.type, view_id: board.id, view_name: board.name });
            this.updateBoardAiContext(board);
          }
        } catch (e) {
          board = null;
        }

        this.setState({ board, loadingBoard: false });
      }
    }

    return board;
  }

  async setPanel(board) {
    const { $auth, $boards } = this.props;
    const { panel: prevPanel } = this.state;

    // If we're not looking at a particular panel, we need to clear this out
    let panel = null;
    let panelCreated = false;

    if (board && this.panelId) {
      // If we have a panelId that isn't e.g. 'ude', then we just get the panel
      if (Number(this.panelId) || this.panelId.startsWith('i_')) {
        // Use the string id directly for optimistic ids (Number('i_xxx') = NaN).
        const lookupId = this.panelId.startsWith('i_') ? this.panelId : Number(this.panelId);
        panel = board.panelCollection.get(lookupId);

        // Otherwise we're building a new panel and using the panelId as the panel type
      } else {
        const defaultPanelType = $auth.hasPermission('ude', { overrideForSudo: false }) ? 'ude' : 'widget';
        const newPanelType =
          this.panelId && $boards.getPanelConfig({ type: this.panelId }) ? this.panelId : defaultPanelType;

        // Don't recreate the same panel you already have!
        if (
          !prevPanel /* if there's no previous panel, then we're creating a new panel */ ||
          !prevPanel.isNew /* if prev panel was not new, then we'd be in the if block above */ ||
          prevPanel.get('type') !== newPanelType /* if it's a different type, it's new! */ ||
          board.panelCollection.size === 0 /* if the board doesn't have a panel, then we're not recreating! */
        ) {
          panel = board.panelCollection.forge({ type: newPanelType, parent_panel_id: this.parentPanelId });
          panelCreated = true;
        } else {
          panel = prevPanel;
        }
      }
    }

    // panel can be null and cleared out here, but we only want to take actions if it has changed
    if (panel !== prevPanel) {
      if (panelCreated && board.isNew) {
        board.panelCollection.add(panel);
      }

      this.setState({ panel });
    }
  }

  get boardId() {
    const { match } = this.props;
    return match.params.boardId || 'new';
  }

  get panelId() {
    const { match } = this.props;
    return match.params.panelId;
  }

  get parentPanelId() {
    const { match } = this.props;
    return match.params.parentPanelId;
  }

  get isUde() {
    const { location } = this.props;
    return location.pathname.startsWith('/v4/ude');
  }

  get basePath() {
    return this.isUde ? '/v4/ude' : '/v4/library';
  }

  handleBoardToolResult(result) {
    if (!result) {
      return;
    }

    const { board } = this.state;
    if (!board || board.isNew) {
      return;
    }

    // Only refresh if the tool result targets this board
    const boardId = String(board.id);
    const resultBoardId = String(result.boardId);
    if (boardId !== resultBoardId) {
      return;
    }

    // Re-fetch the board to get latest data (panels, variables, etc.)
    board.fetch().then(() => {
      this.updateBoardAiContext(board);
    });
  }

  boardAiContext = null;

  updateBoardAiContext(board) {
    const { $journeys } = this.props;

    // Remove previous context if switching boards
    this.removeBoardAiContext();

    const panels = board.panelCollection?.models || [];
    const config = board.get('config') || {};
    const variables = config.variables || [];

    const panelSummaries = panels.map((p) => {
      const panelData = p.get ? p.attributes || p.toJSON() : p;
      const panelConfig = panelData.config || {};
      const data = panelConfig.data || {};
      const parentId = panelData.parent_panel_id;
      const indent = parentId ? '    ' : '';
      const parentNote = parentId ? ` [in section ${parentId}]` : '';
      const parts = [`${indent}- "${panelData.title}" (ID: ${panelData.id}, type: ${panelData.type}${parentNote})`];
      if (panelData.type === 'ude' && data.measurement) {
        const metrics = (data.metrics || []).map((m) => m.name).join(', ');
        const dims = (data.dimensions || []).map((d) => d.name).join(', ');
        const viz = data.viz?.type || 'line';
        parts.push(
          `${indent}  Measurement: ${data.measurement}, Metrics: ${metrics}, Dimensions: ${dims}, Viz: ${viz}`
        );
      } else if (panelData.type === 'text') {
        const content = panelConfig.content || '';
        parts.push(`${indent}  Content: ${content.substring(0, 80)}${content.length > 80 ? '...' : ''}`);
      } else if (panelData.type === 'section') {
        const children = panels.filter((c) => {
          const cd = c.get ? c.attributes || c.toJSON() : c;
          return String(cd.parent_panel_id) === String(panelData.id);
        });
        parts.push(`${indent}  Contains ${children.length} panel(s)`);
      }
      return parts.join('\n');
    });

    const variableSummary = variables.length > 0 ? variables.map((v) => `${v.name} (${v.type})`).join(', ') : 'none';

    this.boardAiContext = {
      type: 'board',
      name: board.get('title') || 'Untitled Board',
      context: [
        'The user is viewing a dashboard/board.',
        `Board Title: ${board.get('title')}`,
        `Board ID: ${board.id}`,
        `Share Level: ${board.get('share_level')}`,
        `Variables (filters): ${variableSummary}`,
        `Panel Count: ${panels.length}`,
        '',
        'Panels:',
        ...panelSummaries
      ].join('\n'),
      suggestions: [
        'What queries are on this dashboard?',
        'Add a chart showing traffic by source IP',
        'Add a Device filter to this dashboard',
        'Remove a panel from this dashboard'
      ]
    };

    $journeys.addChatContext(this.boardAiContext);
  }

  removeBoardAiContext() {
    if (this.boardAiContext) {
      const { $journeys } = this.props;
      $journeys.removeChatContext(this.boardAiContext);
      this.boardAiContext = null;
    }
  }

  handleSaveBoard = async (values = {}, options) => {
    const { $boards, history } = this.props;
    const { board } = this.state;
    const { isNew } = board;

    await board.save(values, options);

    // If it's new or not in the collection, put it in there to prevent losing its in memory state when navigating
    if ($boards.collection.get(board.id) === undefined) {
      $boards.collection.add(board);
    }

    // Let's just make sure it's in recently viewed, because when you're creating a board it won't show up here otherwise.
    // Also, maybe the time should be when you were last in it, not when you _opened_ it. This is a closer approximation.
    // We could wrap in an `isNew` check here if we only want to track saving a new board timing-wise.
    $recentlyViewed.add({ view_type: board.type, view_id: board.id, view_name: board.name });

    if (isNew && !this.panelId) {
      history.replace(`${this.basePath}/boards/${board.id}`);
    }
  };

  // Note: it would be good to abstract this into a function that allows you to save changes on a panel without
  // navigating to it. That would require this to take a `panel` instead of getting it from component state
  handleSavePanel = async (values = {}, options) => {
    const { $boards, history } = this.props;
    const { board, panel } = this.state;

    if (!panel) {
      return;
    }

    const { draft } = options;
    const { isNew: boardIsNew } = board;
    const { isNew: panelIsNew } = panel;

    await panel.save(values, options);

    // If it's new or not in the collection, put it in there to prevent losing its in memory state when navigating
    if (boardIsNew || $boards.collection.get(board.id) === undefined) {
      $boards.collection.add(board);
    }

    // If it's a single panel view, we need to save the board so it's visible in Library
    if (board.singlePanelView && !draft) {
      await board.save({}, { draft, toast: false });

      $recentlyViewed.add({ view_type: board.type, view_id: board.id, view_name: board.name });
    }

    if (boardIsNew || panelIsNew) {
      history.replace(`${this.basePath}/boards/${board.id}/panel/${panel.id}`);
    }
  };

  handleRevertBoard = () => {
    const { board } = this.state;
    return board.discardDraftPanels();
  };

  handleAddPanel = async (type = 'section') => {
    const { history } = this.props;
    const { board } = this.state;

    if (type === 'section') {
      board.addPanel({ title: 'Untitled Section', type });
    } else if (type === 'text') {
      const panel = await board.addPanel({ title: '', type: 'text' });
      this.setState({ editingTextPanel: panel, isNewTextPanel: true });
    } else {
      history.push(`${this.basePath}/boards/${board.id}/panel/${type}`);
    }
  };

  handleEditTextPanel = (panel) => {
    this.setState({ editingTextPanel: panel, isNewTextPanel: false });
  };

  handleSaveTextPanel = async (configUpdates) => {
    const { editingTextPanel: panel } = this.state;
    if (!panel) {
      return;
    }
    const currentConfig = panel.get('config');
    panel.safeSet({ config: { ...currentConfig, ...configUpdates } });
    this.setState({ editingTextPanel: null, isNewTextPanel: false });
    await panel.save({}, { draft: false });
  };

  handleCancelTextPanel = () => {
    const { editingTextPanel: panel, isNewTextPanel } = this.state;
    if (!panel) {
      return;
    }
    if (isNewTextPanel) {
      panel.discardDraft({ toast: false });
    }
    this.setState({ editingTextPanel: null, isNewTextPanel: false });
  };

  get isPanelEditing() {
    const { match } = this.props;
    return match.params.panelId !== undefined;
  }

  render() {
    const { board, loadingBoard, panel, editingTextPanel } = this.state;

    // TODO: debug remove me
    window.board = board;

    return (
      <Suspense
        loading={loadingBoard}
        fallback={
          <Page>
            <Flex mt={2} alignItems="flex-start" justifyContent="center" width="100%">
              <Spinner />
            </Flex>
          </Page>
        }
      >
        {!this.isPanelEditing && (
          <BoardView
            board={board}
            onSaveBoard={this.handleSaveBoard}
            onRevertBoard={this.handleRevertBoard}
            onAddPanel={this.handleAddPanel}
            onEditTextPanel={this.handleEditTextPanel}
          />
        )}
        {this.isPanelEditing && (
          <PanelEditView
            key={panel?.optimisticId}
            board={board}
            panel={panel}
            onSaveBoard={this.handleSaveBoard}
            onSavePanel={this.handleSavePanel}
            onAddPanel={this.handleAddPanel}
          />
        )}
        <TextPanelEditorDialog
          isOpen={!!editingTextPanel}
          config={editingTextPanel ? structuredClone(editingTextPanel.get('config')) : null}
          onSave={this.handleSaveTextPanel}
          onCancel={this.handleCancelTextPanel}
        />
      </Suspense>
    );
  }
}

export default BoardWrapperView;
